import React, { useCallback, useMemo, useState } from 'react';
import { Button, Checkbox, Col, Input, Radio, Row, Tag } from 'antd';
import { CloseOutlined, EditOutlined } from '@ant-design/icons';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import Modal from '../../../components/Modal';
import { ALL_EXCLUDED, ALL_EXCLUDED_RR, SOURCE } from '../../../core/constants';
import Endpoints from '../../../core/constants/endpoints';
import { useActions } from '../../../context/Actions.context';
import { useServices } from '../../../core/services/useServices';
import showActionNotification from '../../../section/Common/utils/showActionNotification';

// Bulk exclusion modal. The UI is currently wired for Bookstat only (see Resize.tsx),
// but the component and backend stay source-aware so it can be extended to other
// sources later by relaxing that single routing check. The "Exclude entire series"
// option is Bookstat-only (other sources have no series concept in the backend).
const reasonForExclude = [
  'Author / Publisher',
  'Foreign Language',
  'Low Performer (manually)',
  'Rights Already Owned',
  'Subject Matter',
  'Audio already published'
];

interface IReason {
  selectionMode: 'tags' | 'series';
  tags: string[];
  isCustom: boolean;
  customTag: string;
  note: string;
}

interface IRowState extends IReason {
  customized: boolean;
}

interface IProps {
  open: boolean;
  onClose: Function;
  onReset: Function;
  id: string;
  selectedRows: any[];
}

const emptyReason = (): IReason => ({
  selectionMode: 'tags',
  tags: [],
  isCustom: false,
  customTag: '',
  note: ''
});

const cloneReason = (reason: IReason): IReason => ({
  selectionMode: reason.selectionMode,
  tags: [...reason.tags],
  isCustom: reason.isCustom,
  customTag: reason.customTag,
  note: reason.note
});

const getIdentifier = (row: any, isBookstat: boolean): string =>
  String((isBookstat ? row?.ASIN : row?.['Fiction Id']) ?? '');

const isReasonValid = (reason: IReason): boolean => {
  if (reason.selectionMode === 'series') {
    return true;
  }

  return reason.tags.length > 0 || (reason.isCustom && reason.customTag.trim().length > 0);
};

// Builds the tag list that is persisted in `excluded_comment` on the backend.
const buildTags = (reason: IReason): string[] => {
  if (reason.selectionMode === 'series') {
    return ['Exclude Entire Series'];
  }

  const custom = reason.isCustom && reason.customTag.trim() ? [reason.customTag.trim()] : [];

  return [...reason.tags, ...custom];
};

/**
 * Reason editor (tags / exclude-entire-series / note). Defined at module scope so its
 * identity is stable across renders — this keeps the note field mounted and focused while
 * the user types (a component defined inside the parent would remount on every keystroke).
 */
const ReasonEditor: React.FC<{
  reason: IReason;
  isBookstat: boolean;
  onChange: (patch: Partial<IReason>) => void;
}> = ({ reason, isBookstat, onChange }) => {
  const onSelectionChange = (e: any) =>
    onChange(
      e?.target?.value === 'series'
        ? { selectionMode: 'series', tags: [], isCustom: false, customTag: '' }
        : { selectionMode: 'tags' }
    );

  // The series explainer is muted while "tags" is selected and darkens once the
  // "Exclude entire series" radio is picked (mirrors the prototype).
  const seriesSelected = reason.selectionMode === 'series';
  const bulletColor = seriesSelected ? 'rgba(0, 0, 0, 0.85)' : 'rgba(0, 0, 0, 0.4)';

  return (
    <>
      <Radio.Group value={reason.selectionMode} onChange={onSelectionChange} style={{ width: '100%' }}>
        <Radio value='tags'>Select tag(s) to exclude title</Radio>

        <div style={{ marginTop: 8, marginLeft: 24 }}>
          <Checkbox.Group
            className='modal-menu'
            value={reason.tags}
            onChange={(values) => onChange({ tags: values as string[] })}
            disabled={reason.selectionMode === 'series'}
          >
            <Row>
              {reasonForExclude.map((item) => (
                <Col span={24} key={item}>
                  <Checkbox value={item}>{item}</Checkbox>
                </Col>
              ))}
              <Col span={24}>
                <Checkbox
                  checked={reason.isCustom}
                  disabled={reason.selectionMode === 'series'}
                  onChange={({ target }) => onChange({ isCustom: target.checked })}
                >
                  <Input
                    disabled={!reason.isCustom || reason.selectionMode === 'series'}
                    placeholder='Create a custom tag'
                    className='modal-input'
                    value={reason.customTag}
                    onChange={({ target }) => onChange({ customTag: target.value })}
                  />
                </Checkbox>
              </Col>
            </Row>
          </Checkbox.Group>
        </div>

        {isBookstat && (
          <div style={{ marginTop: 12 }}>
            <Radio value='series'>Exclude entire series instead</Radio>
            <ul
              style={{
                marginTop: 6,
                marginBottom: 0,
                paddingLeft: 42,
                fontSize: 12,
                lineHeight: 1.6,
                color: bulletColor
              }}
            >
              <li>When selected, the title will be tagged as <code>Exclude Entire Series</code>.</li>
              <li>
                Future titles from this series <strong>will be automatically sent</strong> to the Exclusion List.
              </li>
              <li>
                Other titles from the author that are not part of this series <strong>will not be sent</strong> to
                the Exclusion List.
              </li>
            </ul>
          </div>
        )}
      </Radio.Group>

      <div style={{ marginTop: 12 }}>
        <Input.TextArea
          placeholder='Leave a note about why you want to exclude the title/series'
          maxLength={400}
          value={reason.note}
          onChange={({ target }) => onChange({ note: target.value })}
          rows={4}
        />
        <div style={{ textAlign: 'right', fontSize: 12, marginTop: 4 }}>{reason.note.length} / 400</div>
      </div>
    </>
  );
};

const BulkExclude: React.FC<IProps> = ({ open, onClose, onReset, id, selectedRows }) => {
  const { source: sourcePath, setActionLoading } = useActions();
  const { book: bookService } = useServices();
  const queryClient = useQueryClient();
  const history = useHistory();

  const sourceKey = sourcePath['source'];
  const isBookstat = sourceKey === SOURCE.bookstat;
  const sourceTab = window.location.pathname.split('/')[2];

  const [defaultReason, setDefaultReason] = useState<IReason>(emptyReason());
  const [sameForAll, setSameForAll] = useState<boolean>(true);
  const [rowStates, setRowStates] = useState<Record<string, IRowState>>({});
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<IReason>(emptyReason());
  const [loading, setLoading] = useState<boolean>(false);

  const rows = useMemo(() => selectedRows ?? [], [selectedRows]);

  const getEffectiveReason = (identifier: string): IReason => {
    const rowState = rowStates[identifier];

    if (rowState?.customized) {
      return rowState;
    }

    return defaultReason;
  };

  const canSubmit = useMemo(() => {
    if (!rows.length) {
      return false;
    }

    if (sameForAll) {
      return isReasonValid(defaultReason);
    }

    return rows.every((row) => isReasonValid(getEffectiveReason(getIdentifier(row, isBookstat))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sameForAll, defaultReason, rowStates, isBookstat]);

  const resetState = () => {
    setDefaultReason(emptyReason());
    setSameForAll(true);
    setRowStates({});
    setEditingId(null);
    setDraft(emptyReason());
    setLoading(false);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const updateDefault = useCallback(
    (patch: Partial<IReason>) => setDefaultReason((prev) => ({ ...prev, ...patch })),
    []
  );

  const updateDraft = useCallback(
    (patch: Partial<IReason>) => setDraft((prev) => ({ ...prev, ...patch })),
    []
  );

  const startEdit = (identifier: string) => {
    const rowState = rowStates[identifier];
    setDraft(cloneReason(rowState?.customized ? rowState : defaultReason));
    setEditingId(identifier);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft(emptyReason());
  };

  const applyEdit = (identifier: string) => {
    setRowStates((prev) => ({ ...prev, [identifier]: { ...cloneReason(draft), customized: true } }));
    setEditingId(null);
    setDraft(emptyReason());
  };

  // Revert this row back to inheriting the parent reason.
  const resetRowSelection = (identifier: string) => {
    setRowStates((prev) => {
      const next = { ...prev };
      delete next[identifier];

      return next;
    });
    setEditingId(null);
    setDraft(emptyReason());
  };

  const onSend = async () => {
    setLoading(true);
    setActionLoading(true);

    const items = rows
      .map((row) => {
        const identifier = getIdentifier(row, isBookstat);

        if (!identifier) {
          return null;
        }

        const reason = sameForAll ? defaultReason : getEffectiveReason(identifier);
        const item: Record<string, unknown> = {
          identifier,
          tags: buildTags(reason)
        };

        if (reason.note.trim()) {
          item.note = reason.note.trim();
        }

        if (reason.selectionMode === 'series') {
          item.exclude_series = true;

          if (isBookstat && row?.seriesid) {
            item.seriesid = String(row.seriesid);
          }
        }

        return item;
      })
      .filter(Boolean) as Record<string, unknown>[];

    const excludeName = isBookstat ? ALL_EXCLUDED : ALL_EXCLUDED_RR;
    const endpoints = {
      source: sourceKey,
      sourceTab,
      endpoint: Endpoints[excludeName],
      action: excludeName
    };

    const identifiers = items.map((item) => item.identifier as string);
    const undoParams = isBookstat ? { amazonasin: identifiers } : { fiction_id: identifiers };

    try {
      const response = await bookService.updateTitle(endpoints, {}, { items });

      handleClose();
      onReset();

      if (response?.status === 200) {
        showActionNotification({
          type: 'success',
          message: `Successfully sent ${items.length} title(s) to the Exclusion List.`,
          destination: {
            name: 'All Excluded',
            onGoTo: () => history.push(`${sourceKey}/all-excluded`)
          },
          onUndo: async () => {
            await bookService.updateTitle(
              {
                source: sourceKey,
                sourceTab: 'all-excluded',
                endpoint: Endpoints[id],
                action: id
              },
              undoParams
            );
          },
          queryClient,
          id
        });

        queryClient.refetchQueries(`get-row-count${id}`);
        queryClient.refetchQueries(`get-${id}`);
      } else {
        showActionNotification({ type: 'error', message: 'The title(s) have not been excluded.' });
      }
    } catch (error) {
      handleClose();
      onReset();
      showActionNotification({ type: 'error', message: 'Failed to exclude title(s).' });
      console.error('Failed to bulk exclude title(s)', { id, sourceTab, error });
    } finally {
      setActionLoading(false);
    }
  };

  const renderRowCollapsed = (row: any, identifier: string) => {
    const customized = Boolean(rowStates[identifier]?.customized);
    const chips = buildTags(getEffectiveReason(identifier));

    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ marginRight: 12 }}>
            <div style={{ fontWeight: 600 }}>{row?.Title}</div>
            <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12 }}>{row?.Author}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            {customized && (
              <span
                style={{
                  color: '#d46b08',
                  background: '#fff7e6',
                  border: '1px solid #ffd591',
                  borderRadius: 4,
                  fontSize: 12,
                  padding: '1px 8px'
                }}
              >
                Customized
              </span>
            )}
            <Button
              size='small'
              icon={<EditOutlined />}
              onClick={() => startEdit(identifier)}
              aria-label='Edit reason for this title'
            />
          </div>
        </div>
        {chips.length > 0 && (
          <div style={{ marginTop: 8 }}>
            {chips.map((chip) => (
              <Tag key={chip} style={{ marginBottom: 4 }}>{chip}</Tag>
            ))}
          </div>
        )}
      </>
    );
  };

  const renderRowEditor = (row: any, identifier: string) => (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div style={{ marginRight: 12 }}>
          <div style={{ fontWeight: 600 }}>{row?.Title}</div>
          <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12 }}>{row?.Author}</div>
        </div>
        <Button type='text' size='small' icon={<CloseOutlined />} onClick={cancelEdit} aria-label='Close editor' />
      </div>

      <ReasonEditor reason={draft} isBookstat={isBookstat} onChange={updateDraft} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
        <Button type='link' style={{ paddingLeft: 0 }} onClick={() => resetRowSelection(identifier)}>
          Reset selection
        </Button>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button onClick={cancelEdit}>Cancel</Button>
          <Button type='primary' disabled={!isReasonValid(draft)} onClick={() => applyEdit(identifier)}>
            Apply
          </Button>
        </div>
      </div>
    </>
  );

  return (
    <Modal
      okText='Send to Exclusion List'
      title='Send to Exclusion List'
      visible={open}
      onOk={onSend}
      okButtonProps={{ disabled: !canSubmit || loading }}
      confirmLoading={loading}
      onCancel={handleClose}
      width={560}
    >
      <ReasonEditor reason={defaultReason} isBookstat={isBookstat} onChange={updateDefault} />

      <div style={{ marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
        <Checkbox checked={sameForAll} onChange={({ target }) => setSameForAll(target.checked)}>
          Use same reason for all titles
        </Checkbox>
        {!sameForAll && (
          <div style={{ marginTop: 4, marginLeft: 24, color: 'rgba(0,0,0,0.45)', fontSize: 12 }}>
            Rows inherit the above selection automatically. Customized rows have their own reason and stay
            untouched by changes above.
          </div>
        )}
      </div>

      {!sameForAll && (
        <div style={{ marginTop: 12, maxHeight: 360, overflowY: 'auto' }}>
          {rows.map((row) => {
            const identifier = getIdentifier(row, isBookstat);
            const isEditing = editingId === identifier;

            return (
              <div
                key={identifier || row?.Title}
                style={{ border: '1px solid #f0f0f0', borderRadius: 4, padding: 12, marginBottom: 8 }}
              >
                {isEditing ? renderRowEditor(row, identifier) : renderRowCollapsed(row, identifier)}
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
};

export default BulkExclude;
